/**
 * Shared GLB-instance renderer for custom assets and mover tools.
 *
 * Opacity propagates to all materials in the cloned hierarchy
 * (material.transparent = true, material.opacity = value, depthWrite =
 * (opacity == 1)).
 *
 * Clone-on-write: drei's `useGLTF` caches the parsed glTF globally, and
 * `cloneSkeleton` shares materials by reference across every instance
 * that uses the same URL. Mutating `material.opacity` directly would
 * leak this asset's opacity onto every other consumer of the same GLB
 * — e.g. setting `opacity: 0.95` on a custom asset that reuses the
 * `AT2000-0250.glb` as cargo would also turn the actual AT2000 modules
 * in the track translucent. We clone the material(s) before mutating
 * (only when opacity < 1, to keep the fully-opaque path zero-cost) and
 * restore + dispose on cleanup.
 */

import React, { useContext, useEffect, Suspense } from 'react';
import { Material, Mesh } from 'three';
import { useGltfClone } from './useGltfClone.js';
import { useGltfHighlight } from './useGltfHighlight.js';
import { SelectionTintContext } from './SelectionTintContext.js';

interface CustomAssetMeshProps {
  glbUrl: string;
  positionMm?: [number, number, number];
  rotationDegEuler?: [number, number, number];
  scale?: number;
  opacity?: number;
  visible?: boolean;
  /** Wireframe placeholder size when GLB is unavailable. */
  placeholderSizeMm?: [number, number, number];
}

const DEG2RAD = Math.PI / 180;

const Inner: React.FC<CustomAssetMeshProps> = ({
  glbUrl,
  positionMm = [0, 0, 0],
  rotationDegEuler = [0, 0, 0],
  scale = 1,
  opacity = 1,
  visible = true,
  placeholderSizeMm = [50, 50, 50],
}) => {
  const gltf = useGltfClone(glbUrl ?? '');

  // Inherit the parent mover's selection tint when present (AT8200
  // tools + assets bound under <XtsMover>). Top-level static custom
  // assets sit outside any XtsMover and see `null` here, so they're
  // never tinted — that's correct, only mover-bound children should
  // pick up the parent's selection state. Drive-status blink stays
  // on the parent mover's own GLB; we deliberately don't propagate
  // it to children here.
  const tint = useContext(SelectionTintContext);
  useGltfHighlight(gltf, {
    selected: tint?.selected ?? false,
    selectionColor: tint?.color ?? '#FFE090',
  });

  useEffect(() => {
    // Fully opaque → no mutation needed, the shared material is fine.
    if (!gltf || opacity >= 1) return;
    type Swap = {
      mesh: Mesh;
      original: Material | Material[];
      cloned: Material | Material[];
    };
    const swaps: Swap[] = [];
    const cloneOne = (m: Material): Material => {
      const c = m.clone();
      c.transparent = true;
      c.opacity = opacity;
      c.depthWrite = false;
      c.needsUpdate = true;
      return c;
    };
    gltf.scene.traverse((obj) => {
      const mesh = obj as Mesh;
      if (!mesh.isMesh || !mesh.material) return;
      const cloned: Material | Material[] = Array.isArray(mesh.material)
        ? mesh.material.map(cloneOne)
        : cloneOne(mesh.material);
      swaps.push({ mesh, original: mesh.material, cloned });
      mesh.material = cloned;
    });
    return () => {
      for (const { mesh, original, cloned } of swaps) {
        mesh.material = original;
        if (Array.isArray(cloned)) cloned.forEach((c) => c.dispose());
        else cloned.dispose();
      }
    };
  }, [gltf, opacity]);

  if (!visible) return null;

  return (
    <group
      position={positionMm}
      rotation={[
        rotationDegEuler[0] * DEG2RAD,
        rotationDegEuler[1] * DEG2RAD,
        rotationDegEuler[2] * DEG2RAD,
        'XYZ',
      ]}
      scale={scale}
    >
      {gltf ? (
        <primitive object={gltf.scene} />
      ) : (
        <mesh>
          <boxGeometry args={placeholderSizeMm} />
          <meshBasicMaterial color="#88FF88" wireframe transparent opacity={0.6} />
        </mesh>
      )}
    </group>
  );
};

export const CustomAssetMesh: React.FC<CustomAssetMeshProps> = (props) => (
  <Suspense fallback={null}>
    <Inner {...props} />
  </Suspense>
);
